import { css } from "@emotion/css";
import { Checkbox } from "@octopusdeploy/design-system-components";
import { DeprecatedSelect, type DeprecatedDropdownMenuOption } from "@octopusdeploy/design-system-octopus-components";
import { text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { EnvironmentV2Resource, ProjectResource } from "@octopusdeploy/octopus-server-client";
import { Permission, TenantedDeploymentMode } from "@octopusdeploy/octopus-server-client";
import PermissionCheck from "~/components/PermissionCheck/PermissionCheck";
import styles from "./style.module.less";
const headerStyle = css({
    font: text.body.bold.medium,
    color: themeTokens.color.text.primary,
});
interface VariableScopeSelectorProps {
    project: ProjectResource;
    environments: EnvironmentV2Resource[];
    environmentId?: string;
    tenants: DeprecatedDropdownMenuOption[];
    tenantId?: string;
    actions: DeprecatedDropdownMenuOption[];
    actionId?: string;
    channels: DeprecatedDropdownMenuOption[];
    channelId?: string;
    machines: DeprecatedDropdownMenuOption[];
    machineId?: string;
    showOctopus: boolean;
    filterEmptyValues: boolean;
    processType: "deployment" | "runbook";
    runbooks?: DeprecatedDropdownMenuOption[];
    runbookId?: string;
    noIndent?: boolean;
    onEnvironmentChanged: (environmentId: string | undefined) => void;
    onTenantChanged: (tenantId: string | undefined) => void;
    onActionChanged: (actionId: string | undefined) => void;
    onChannelChanged: (channelId: string | undefined) => void;
    onMachineChanged: (machineId: string | undefined) => void;
    onShowOctopusChanged: (showOctopus: boolean) => void;
    onFilterEmptyValuesChanged: (filterEmptyValues: boolean) => void;
    onProcessTypeChanged: (processType: "deployment" | "runbook") => void;
    onRunbookChanged: (runbookId: string | undefined) => void;
}
export function VariableScopeSelector({ project, environments, environmentId, tenants, tenantId, actions, actionId, channels, channelId, machines, machineId, showOctopus, filterEmptyValues, processType, runbooks, runbookId, noIndent = false, onEnvironmentChanged, onTenantChanged, onActionChanged, onChannelChanged, onMachineChanged, onShowOctopusChanged, onFilterEmptyValuesChanged, onProcessTypeChanged, onRunbookChanged, }: VariableScopeSelectorProps) {
    const processTypeOptions = [{ value: "deployment", text: "Deployment Process" }, ...(runbooks && runbooks.length > 0 ? [{ value: "runbook", text: "Runbook" }] : [])];
    const handleProcessTypeChange = (value: string | undefined) => {
        if (value === "deployment" || value === "runbook") {
            onProcessTypeChanged(value);
        }
    };
    return (<div className={noIndent ? styles.scopeRowNoIndent : styles.scopeRow}>
            <div className={headerStyle}>Select Execution Scenario</div>
            <DeprecatedSelect label="Process Type" items={processTypeOptions} allowClear={false} value={processType} onChange={handleProcessTypeChange}/>

            {processType === "deployment" && (<div>
                    <DeprecatedSelect label="Environment" items={environments.map((env) => ({ value: env.Id, text: env.Name }))} allowClear={false} value={environmentId} onChange={onEnvironmentChanged} sortItems={true}/>
                    {tenants && tenants.length > 0 && (<DeprecatedSelect label="Tenant" items={tenants} allowClear={project.TenantedDeploymentMode !== TenantedDeploymentMode.Tenanted} value={tenantId} onChange={onTenantChanged} placeholder="Untenanted"/>)}
                    <PermissionCheck permission={Permission.ProcessView} project={project.Id} wildcard={true}>
                        <DeprecatedSelect label="Process Step" items={actions} allowClear={false} value={actionId} onChange={onActionChanged}/>
                        {channels && channels.length > 1 && <DeprecatedSelect label="Channel" items={channels} allowClear={false} value={channelId} onChange={onChannelChanged}/>}
                    </PermissionCheck>
                </div>)}

            {processType === "runbook" && (<div>
                    {runbooks && runbooks.length > 0 && <DeprecatedSelect label="Runbook" items={runbooks} allowClear={false} value={runbookId} onChange={onRunbookChanged}/>}
                    <DeprecatedSelect label="Environment" items={environments.map((env) => ({ value: env.Id, text: env.Name }))} allowClear={false} value={environmentId} onChange={onEnvironmentChanged} sortItems={true}/>
                    {tenants && tenants.length > 0 && (<DeprecatedSelect label="Tenant" items={tenants} allowClear={project.TenantedDeploymentMode !== TenantedDeploymentMode.Tenanted} value={tenantId} onChange={onTenantChanged} placeholder="Untenanted"/>)}
                </div>)}

            <DeprecatedSelect label="Deployment target" items={machines} allowFilter={true} allowClear={true} value={machineId} onChange={onMachineChanged} placeholder="Select deployment target"/>
            <div className={styles.showAll}>
                <Checkbox label="Show system variables" value={showOctopus} onChange={onShowOctopusChanged}/>
                <Checkbox label="Filter empty values" value={filterEmptyValues} onChange={onFilterEmptyValuesChanged}/>
            </div>
        </div>);
}
